import React, { useState } from 'react';
import Image from '../components/AppImage';
import Icon from '../components/AppIcon';

// Scoped background style for this page only
const contactPageBgStyle = `
.contact-page {
  background: linear-gradient(135deg, #233c2e 0%, #2e2540 100%);
  min-height: 100vh;
}
`;

const ContactPage = () => {
  // --- Contact form state and handlers ---
  const [form, setForm] = useState({ name: "", studentClass: "", message: "", responseEmail: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
  };

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSent(false);
    try {
      const res = await fetch(`${API_BASE_URL}/api/support/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSent(true);
        setForm({ name: "", studentClass: "", message: "", responseEmail: "" });
      } else {
        setError(data.message || "Failed to send message.");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    }
    setLoading(false);
  };
  // --- End contact form state and handlers ---

  return (
    <>
      <style>{contactPageBgStyle}</style>
      <div className="contact-page min-h-screen bg-background">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto bg-card border border-border rounded-xl shadow-lg p-8">
            <div className="flex flex-col items-center mb-8">
              <div className="w-20 h-20 mb-4 rounded-full overflow-hidden border-4 border-primary/20">
                <Image 
                  src="/assets/images/school_logo-1754996036714.jpg"
                  alt="Anglican College Owerrinta Logo"
                  className="w-full h-full object-cover"
                />
              </div>
              <h1 className="text-3xl font-heading font-bold text-primary mb-2">Contact Info</h1>
              <p className="text-muted-foreground text-center">
                Reach out to us for inquiries, support, or more information about Anglican College Owerrinta.<br />
                We are always happy to hear from you and will respond as soon as possible.<br />
                For urgent matters, please use the phone numbers below.
              </p>
            </div>
            <ul className="space-y-4 font-body text-base text-foreground mb-8">
              <li className="flex items-center space-x-3">
                <Icon name="MapPin" size={20} />
                <span>P.O Box 15, Owerrinta, Isiala Ngwa South, Abia State, Nigeria</span>
              </li>
              <li>
                <span className="font-semibold text-primary">For School-Related Issues:</span>
                <ul className="ml-4 mt-1 space-y-1 text-sm">
                  <li className="flex items-center space-x-2">
                    <Icon name="Phone" size={18} />
                    <span>
                      <a
                        href="/call?number=+2348166993771"
                        className="underline text-primary hover:text-primary/80 transition"
                      >
                        +2348166993771
                      </a>
                    </span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Icon name="Phone" size={18} />
                    <span>
                      <a
                        href="/call?number=+2349077364848"
                        className="underline text-primary hover:text-primary/80 transition"
                      >
                        +2349077364848
                      </a>
                    </span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Icon name="Phone" size={18} />
                    <span>
                      <a
                        href="/call?number=+2348063425289"
                        className="underline text-primary hover:text-primary/80 transition"
                      >
                        +2348063425289
                      </a>
                    </span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span>
                    </span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Icon name="Mail" size={18} />
                    <span>
                      <a
                        href="mailto:anglicancollegesnrsch@gmail.com"
                        className="underline text-primary hover:text-primary/80 transition"
                      >
                        anglicancollegesnrsch@gmail.com
                      </a>
                    </span>
                  </li>
                </ul>
              </li>
              <li>
                <span className="font-semibold text-primary">For Website/Technical Issues:</span>
                <ul className="ml-4 mt-1 space-y-1 text-sm mb-4">
                  <li className="flex items-center space-x-2">
                    <Icon name="Smartphone" size={18} />
                    <span>
                      WhatsApp: <a href="https://wa.me/2348143316441" className="underline text-primary">+2348143316441</a>
                    </span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Icon name="Phone" size={18} />
                    <span>
                      <a
                        href="/call?number=+2349040744911"
                        className="underline text-primary hover:text-primary/80 transition"
                      >
                        Call: +2349040744911
                      </a>
                    </span>
                  </li>
                </ul>
                {/* Contact Form */}
                <form className="mt-4 grid gap-4 bg-muted/10 border border-border rounded-xl p-4" onSubmit={handleSubmit}>
                  <div className="font-semibold mb-2 text-primary">Send a message to technical support:</div>
                  <div className="text-sm text-muted-foreground mb-2 font-semibold">
                    Please describe your issues very well and go straight to the point.<br />
                    When you send us a message, an expert will attend to your request within 30 minutes.
                  </div>
                  <input
                    name="name"
                    className="rounded-xl bg-background border border-border px-4 py-3 outline-none text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary"
                    placeholder="Student name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    autoComplete="off"
                  />
                  <div className="text-xs text-muted-foreground font-semibold -mt-3 mb-2">
                    Enter the full name of the student.
                  </div>
                  <input
                    name="studentClass"
                    className="rounded-xl bg-background border border-border px-4 py-3 outline-none text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary"
                    placeholder="Student's class"
                    type="text"
                    value={form.studentClass || ""}
                    onChange={handleChange}
                    required
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    inputMode="text"
                  />
                  <div className="text-xs text-muted-foreground font-semibold -mt-3 mb-2">
                    Enter the current class of the student (e.g., JSS1, SS2).
                  </div>
                  <input
                    name="responseEmail"
                    className="rounded-xl bg-background border border-border px-4 py-3 outline-none text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary"
                    placeholder="Email to receive response"
                    type="email"
                    value={form.responseEmail || ""}
                    onChange={handleChange}
                    required
                  />
                  <div className="text-xs text-muted-foreground font-semibold -mt-3 mb-2">
                    Enter a valid email address where you want to receive our response.
                  </div>
                  <textarea
                    name="message"
                    className="rounded-xl bg-background border border-border px-4 py-3 h-32 outline-none text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary"
                    placeholder="Describe your issue"
                    value={form.message}
                    onChange={handleChange}
                    required
                  />
                  <div className="text-xs text-muted-foreground font-semibold -mt-3 mb-2">
                    Clearly describe the issue you are facing.
                  </div>
                  <button
                    className="rounded-xl px-5 py-3 font-semibold bg-gradient-to-r from-primary via-secondary to-primary text-white w-fit shadow hover:from-secondary hover:to-primary"
                    disabled={loading}
                  >
                    {loading ? "Sending..." : "Send message"}
                  </button>
                  {sent && (
                    <div className="mt-2 text-emerald-600 font-semibold">
                      Message sent successfully.<br />
                      An expert will attend to your request within 20/30 minutes.
                    </div>
                  )}
                  {error && (
                    <div className="mt-2 text-error font-semibold">{error}</div>
                  )}
                </form>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactPage;